function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]);

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { nanoid } from "nanoid";
import useField from "../hooks/internal/useField";
import { mergeProps, areOptionsValid } from "../utils/component";
import Field from "./internal/Field";
import InternalSelect from "./internal/InternalSelect";
import { jsx as ___EmotionJSX } from "@emotion/core";
var COLORS = InternalSelect.COLORS;

function isOptionSelected(options, value) {
  return options.findIndex(function (option) {
    return option.value === value;
  }) > -1;
}

var DEFAULT_PROPS = {
  color: InternalSelect.DEFAULT_PROPS.color,
  placeholder: InternalSelect.DEFAULT_PROPS.placeholder,
  fullWidth: InternalSelect.DEFAULT_PROPS.fullWidth,
  disabled: false,
  optional: false,
  validate: function validate(value, _ref) {
    var isEmpty = _ref.isEmpty;

    if (isEmpty(value)) {
      return "Please make a selection.";
    }

    return null;
  }
};
Select.COLORS = COLORS;
Select.DEFAULT_PROPS = DEFAULT_PROPS;

function Select(props) {
  var mergedProps = mergeProps(props, DEFAULT_PROPS, {}, {
    color: function color(_color) {
      return COLORS.includes(_color);
    },
    placeholder: function placeholder(_placeholder) {
      return typeof _placeholder === "string";
    },
    fullWidth: function fullWidth(_fullWidth) {
      return typeof _fullWidth === "boolean";
    },
    helpText: function helpText(_helpText) {
      return typeof _helpText === "string";
    },
    disabled: function disabled(_disabled) {
      return typeof _disabled === "boolean";
    },
    optional: function optional(_optional) {
      return typeof _optional === "boolean";
    },
    options: function options(_options) {
      return areOptionsValid(_options);
    },
    onChange: function onChange(_onChange) {
      return typeof _onChange === "function";
    }
  });
  var name = mergedProps.name,
      label = mergedProps.label,
      placeholder = mergedProps.placeholder,
      options = mergedProps.options,
      fullWidth = mergedProps.fullWidth,
      helpText = mergedProps.helpText,
      disabled = mergedProps.disabled,
      optional = mergedProps.optional,
      validate = mergedProps.validate,
      validateData = mergedProps.validateData,
      propsOnChange = mergedProps.onChange,
      testId = mergedProps.testId,
      __internal__focus = mergedProps.__internal__focus;

  if (!options) {
    throw new Error("Select options should have the following format: [{ label: \"option-label\", value: \"option-value\" }, ...]");
  }

  var _useState = useState(function () {
    return "select-".concat(nanoid());
  }),
      _useState2 = _slicedToArray(_useState, 1),
      selectId = _useState2[0];

  var _useState3 = useState(function () {
    return "select-aux-".concat(nanoid());
  }),
      _useState4 = _slicedToArray(_useState3, 1),
      auxId = _useState4[0];

  var isEmpty = useCallback(function (value) {
    return isOptionSelected(options, value) === false;
  }, [options]);
  var data = useMemo(function () {
    return _objectSpread({
      isEmpty: isEmpty
    }, validateData && {
      data: validateData
    });
  }, [isEmpty, validateData]);

  var _useField = useField("Select", {
    name: name,
    disabled: disabled,
    optional: optional,
    validate: validate,
    data: data
  }),
      value = _useField.value,
      errors = _useField.errors,
      hasErrors = _useField.hasErrors,
      onFocus = _useField.onFocus,
      onBlur = _useField.onBlur,
      fieldOnChange = _useField.onChange;

  var onChange = function onChange(event) {
    fieldOnChange(event);
    var selectedValue = event.target.value;
    var selectedOption = options.find(function (_ref2) {
      var value = _ref2.value;
      return value === selectedValue;
    });
    propsOnChange && propsOnChange({
      selectedOption: selectedOption
    });
  };

  return ___EmotionJSX(Field, {
    fullWidth: fullWidth,
    optional: optional,
    disabled: disabled,
    label: label,
    labelFor: selectId,
    auxId: auxId,
    helpText: helpText,
    errors: errors,
    testId: testId
  }, ___EmotionJSX(InternalSelect, {
    id: selectId,
    name: name,
    color: props.color,
    placeholder: placeholder,
    options: options,
    fullWidth: fullWidth,
    optional: optional,
    disabled: disabled,
    isValid: !hasErrors,
    describedBy: helpText || hasErrors ? auxId : null,
    onFocus: onFocus,
    onBlur: onBlur,
    value: value,
    onChange: onChange,
    __internal__focus: __internal__focus
  }));
}

Select.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  color: PropTypes.oneOf(COLORS),
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })).isRequired,
  fullWidth: PropTypes.bool,
  helpText: PropTypes.string,
  disabled: PropTypes.bool,
  optional: PropTypes.bool,
  validate: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  validateData: PropTypes.any,
  onChange: PropTypes.func,
  testId: PropTypes.string,
  __internal__focus: PropTypes.bool
};
export default Select;